import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import PaymentBreakdownTable from '../components/PaymentBreakdownTable';
import { DEFAULT_PLATFORM_FEES } from '../utils/feeCalculation';

const InvitationSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const { login } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invitationData, setInvitationData] = useState<any>(null);

  // Get invitation token from URL
  const invitationToken = token;

  useEffect(() => {
    const fetchInvitationData = async () => {
      if (!invitationToken) return;
      
      try {
        const response = await apiService.get(`/invitations/${invitationToken}`);
        
        if (response.success) {
          const data = response.data as any;
          
          // Map serviceDetails to services for display
          const mappedData = {
            ...data,
            services: data.serviceDetails?.services || data.services || {},
            frequency: data.serviceDetails?.frequency || data.customer?.serviceSchedule?.frequency || data.frequency || 'weekly',
            price: data.customer?.price || data.price || 0,
            servicePricing: data.customer?.servicePricing || null,
          };
          
          setInvitationData(mappedData);
          
          if (data.customer) {
            // Format phone number to (xxx) xxx-xxxx
            const formatPhone = (phone: string) => {
              const cleaned = phone.replace(/\D/g, '');
              if (cleaned.length === 10) {
                return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
              }
              return phone;
            };
            
            setFormData(prev => ({
              ...prev,
              firstName: data.customer.firstName || '',
              lastName: data.customer.lastName || '',
              email: data.customer.email || '',
              phone: formatPhone(data.customer.phone || ''),
            }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch invitation data:', error);
        setError('Invalid invitation link. Please contact your service provider.');
      }
    };

    if (invitationToken) {
      fetchInvitationData();
    }
  }, [invitationToken]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.post('/auth/register', {
        ...formData,
        userType: 'customer',
        invitationToken,
      });

      if (response.success) {
        setSuccess('Account created successfully! Logging you in...');
        const loginResponse = await apiService.post('/auth/login', {
          email: formData.email,
          password: formData.password,
        });

        if (loginResponse.success) {
          await login(formData.email, formData.password);
          // If invited, accept onboarding now
          if (invitationToken) {
            try {
              await apiService.post('/existing-customers/onboard/accept', { token: invitationToken });
            } catch (e) {
              console.error('Accept onboarding failed (non-fatal):', e);
            }
          }
          setTimeout(() => {
            // Provider-onboarded customers: go to My Services to approve/decline the provider offer
            navigate('/customer?onboarding=1', { replace: true });
          }, 2000);
        }
      } else {
        // Check if it's a duplicate email error
        if (response.error && response.error.includes('already exists')) {
          setError('An account with this email already exists. Please log in to accept the invitation.');
          // Show a button to go to login with the token in the URL
          setTimeout(() => {
            navigate(`/login?invitationToken=${invitationToken}`);
          }, 3000);
        } else {
          setError(response.error || 'Failed to create account');
        }
      }
    } catch (err: any) {
      // Check if it's a duplicate email error
      if (err.message && err.message.includes('already exists')) {
        setError('An account with this email already exists. Please log in to accept the invitation.');
        // Show a button to go to login with the token in the URL
        setTimeout(() => {
          navigate(`/login?invitationToken=${invitationToken}`);
        }, 3000);
      } else {
        setError(err.message || 'Failed to create account');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      component="main" 
      maxWidth="sm" 
      sx={{ 
        py: isMobile ? 2 : 4,
        px: isMobile ? 1 : 3,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: isMobile ? 3 : 4, 
          width: '100%',
          borderRadius: isMobile ? 2 : 3,
          maxWidth: isMobile ? '100%' : '500px'
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            component="h1" 
            variant={isMobile ? "h5" : "h4"} 
            gutterBottom
            sx={{ fontWeight: 'bold', color: 'primary.main' }}
          >
            Welcome to LawnHub!
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
          >
            {invitationData 
              ? `Set up your free account for ${invitationData.providerName}'s services`
              : 'Create your free account to get started'
            }
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: isMobile ? '0.85rem' : '0.9rem' }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, fontSize: isMobile ? '0.85rem' : '0.9rem' }}>
            {success}
          </Alert>
        )}

        {invitationData && (
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '1rem' : '1.1rem' }}>
                Service Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Provider:</strong> {invitationData.providerName}
              </Typography>
              {Object.entries(invitationData.services || {})
                .filter(([_, enabled]) => enabled)
                .map(([serviceKey, _]) => {
                  const serviceNames: { [key: string]: string } = {
                    lawnMowingTrimming: 'Lawn Mowing/Trimming',
                    weedControlFertilizing: 'Weed Control & Fertilizing',
                    snowRemoval: 'Snow Removal'
                  };
                  const serviceName = serviceNames[serviceKey] || serviceKey;
                  
                  // Get frequency and price from servicePricing if available, otherwise use defaults
                  let frequency = invitationData.frequency || 'As scheduled';
                  let price = '$0.00';
                  
                  if (invitationData.servicePricing && invitationData.servicePricing[serviceKey]) {
                    const servicePricing = invitationData.servicePricing[serviceKey];
                    frequency = servicePricing.frequency || frequency;
                    price = servicePricing.price ? `$${servicePricing.price.toFixed(2)}` : price;
                  }
                  
                  return (
                    <Typography key={serviceKey} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {serviceName} / {frequency} / {price}
                    </Typography>
                  );
                })}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                What you&apos;ll be charged
              </Typography>
              <PaymentBreakdownTable
                compact
                lines={Object.entries(invitationData.services || {})
                  .filter(([_, enabled]) => enabled)
                  .map(([serviceKey]) => {
                    const serviceNames: Record<string, string> = {
                      lawnMowingTrimming: 'Lawn Mowing/Trimming',
                      weedControlFertilizing: 'Weed Control & Fertilizing',
                      snowRemoval: 'Snow Removal',
                    };
                    const sp = invitationData.servicePricing?.[serviceKey];
                    const servicePrice = sp?.price ?? invitationData.price ?? 0;
                    return {
                      label: serviceNames[serviceKey] || serviceKey,
                      servicePrice: Number(servicePrice) || 0,
                      platformFeePercent: DEFAULT_PLATFORM_FEES.importedCustomerFeePercentage,
                      chargeNote: 'per visit',
                    };
                  })}
              />
            </CardContent>
          </Card>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  '& .MuiInputLabel-root': { fontSize: isMobile ? '0.85rem' : '0.9rem' },
                  '& .MuiInputBase-input': { fontSize: isMobile ? '0.9rem' : '1rem' }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  '& .MuiInputLabel-root': { fontSize: isMobile ? '0.85rem' : '0.9rem' },
                  '& .MuiInputBase-input': { fontSize: isMobile ? '0.9rem' : '1rem' }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  '& .MuiInputLabel-root': { fontSize: isMobile ? '0.85rem' : '0.9rem' },
                  '& .MuiInputBase-input': { fontSize: isMobile ? '0.9rem' : '1rem' }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  '& .MuiInputLabel-root': { fontSize: isMobile ? '0.85rem' : '0.9rem' },
                  '& .MuiInputBase-input': { fontSize: isMobile ? '0.9rem' : '1rem' }
                }}
              />
            </Grid>
            
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.85rem' }}>
                  Create Password
                </Typography>
              </Divider>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                helperText="Must be at least 6 characters"
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  '& .MuiInputLabel-root': { fontSize: isMobile ? '0.85rem' : '0.9rem' },
                  '& .MuiInputBase-input': { fontSize: isMobile ? '0.9rem' : '1rem' },
                  '& .MuiFormHelperText-root': { fontSize: isMobile ? '0.75rem' : '0.8rem' }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  '& .MuiInputLabel-root': { fontSize: isMobile ? '0.85rem' : '0.9rem' },
                  '& .MuiInputBase-input': { fontSize: isMobile ? '0.9rem' : '1rem' }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                size={isMobile ? "large" : "large"}
                sx={{
                  py: isMobile ? 1.5 : 2,
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Create Free Account'
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.85rem' }}>
            By creating an account, you agree to our{' '}
            <Link href="/legal?tab=customer" target="_blank" rel="noopener">
              Customer Service Agreement
            </Link>{' '}
            and{' '}
            <Link href="/privacy-policy" target="_blank" rel="noopener">
              Privacy Policy
            </Link>
            .
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default InvitationSignupPage;